import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { statementTemplateService } from '../../services/api/statementTemplateService';
import StatementTemplateBuilder from './StatementTemplateBuilder';

const ResizableTableCell = ({ children, width, onResize, ...props }) => (
  <TableCell
    {...props}
    sx={{
      width: width,
      minWidth: width,
      maxWidth: width,
      position: 'relative',
      padding: '8px',
      ...props.sx
    }}
  >
    {children}
    {onResize && (
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '5px',
          cursor: 'col-resize',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3
          }
        }}
      />
    )}
  </TableCell>
);

const StatementTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [templateToClone, setTemplateToClone] = useState(null);
  const [cloneFormData, setCloneFormData] = useState({ template_id: '', name: '' });
  const [columnWidths, setColumnWidths] = useState({
    templateId: 200,
    name: 250,
    version: 80,
    status: 100,
    created: 150,
    actions: 80
  });
  const [resizing, setResizing] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [activeFilters, allTemplates]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await statementTemplateService.listTemplates();
      setAllTemplates(response.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (activeFilters.size === 0) {
      setTemplates(allTemplates);
    } else {
      const filtered = allTemplates.filter(template =>
        activeFilters.has(template.status)
      );
      setTemplates(filtered);
    }
  };

  const toggleFilter = (status) => {
    setActiveFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setActiveFilters(new Set());
  };

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleActivate = async () => {
    if (!selectedTemplate) return;

    try {
      await statementTemplateService.activateTemplate(selectedTemplate.template_id);
      await loadTemplates();
      handleMenuClose();
    } catch (err) {
      console.error('Error activating template:', err);
      setError(err.message || 'Failed to activate template');
    }
  };

  const handleArchive = async () => {
    if (!selectedTemplate) return;

    try {
      await statementTemplateService.archiveTemplate(selectedTemplate.template_id);
      await loadTemplates();
      handleMenuClose();
    } catch (err) {
      console.error('Error archiving template:', err);
      setError(err.message || 'Failed to archive template');
    }
  };

  const handleClone = () => {
    if (!selectedTemplate) return;

    // Store the template to clone
    setTemplateToClone(selectedTemplate);

    // Pre-fill clone dialog with modified template ID and name
    setCloneFormData({
      template_id: `${selectedTemplate.template_id}-copy`,
      name: `${selectedTemplate.name} (Copy)`
    });

    handleMenuClose();
    setCloneDialogOpen(true);
  };

  const handleConfirmClone = async () => {
    if (!templateToClone || !cloneFormData.template_id || !cloneFormData.name) {
      setError('Template ID and name are required');
      return;
    }

    try {
      // Clone as a new template with the specified ID and name
      await statementTemplateService.cloneTemplate(
        templateToClone.template_id,
        {
          template_id: cloneFormData.template_id,
          name: cloneFormData.name
        }
      );
      await loadTemplates();
      setCloneDialogOpen(false);
      setTemplateToClone(null);
      setCloneFormData({ template_id: '', name: '' });
    } catch (err) {
      console.error('Error cloning template:', err);
      setError(err.message || 'Failed to clone template');
    }
  };

  const handleCancelClone = () => {
    setCloneDialogOpen(false);
    setTemplateToClone(null);
    setCloneFormData({ template_id: '', name: '' });
  };

  const handleCreateTemplate = () => {
    setEditingTemplateId(null);
    setViewMode(false);
    setShowBuilder(true);
  };

  const handleEditTemplate = () => {
    if (!selectedTemplate) return;
    setEditingTemplateId(selectedTemplate.template_id);
    setViewMode(false);
    setShowBuilder(true);
    handleMenuClose();
  };

  const handleViewTemplate = () => {
    if (!selectedTemplate) return;
    setEditingTemplateId(selectedTemplate.template_id);
    setViewMode(true);
    setShowBuilder(true);
    handleMenuClose();
  };

  const handleBuilderSave = async () => {
    setShowBuilder(false);
    setEditingTemplateId(null);
    setViewMode(false);
    await loadTemplates();
  };

  const handleBuilderCancel = () => {
    setShowBuilder(false);
    setEditingTemplateId(null);
    setViewMode(false);
  };

  const handleMouseDown = (columnKey) => (e) => {
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (showBuilder) {
    return (
      <StatementTemplateBuilder
        templateId={editingTemplateId}
        readOnly={viewMode}
        onSave={handleBuilderSave}
        onCancel={handleBuilderCancel}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Typography variant="h5">Witness Statement Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Create Template
        </Button>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Filter:
        </Typography>
        <Chip
          label="Active"
          onClick={() => toggleFilter('active')}
          color="success"
          variant={activeFilters.has('active') ? 'filled' : 'outlined'}
        />
        <Chip
          label="Draft"
          onClick={() => toggleFilter('draft')}
          color="warning"
          variant={activeFilters.has('draft') ? 'filled' : 'outlined'}
        />
        <Chip
          label="Archived"
          onClick={() => toggleFilter('archived')}
          color="default"
          variant={activeFilters.has('archived') ? 'filled' : 'outlined'}
        />
        {activeFilters.size > 0 && (
          <Button
            size="small"
            onClick={clearFilters}
            sx={{ ml: 1 }}
          >
            Clear All
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <ResizableTableCell width={columnWidths.templateId} onResize={handleMouseDown('templateId')}>
                Template ID
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.name} onResize={handleMouseDown('name')}>
                Name
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.version} onResize={handleMouseDown('version')}>
                Version
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.status} onResize={handleMouseDown('status')}>
                Status
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.created} onResize={handleMouseDown('created')}>
                Created
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.actions}>
                Actions
              </ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <ResizableTableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No templates found
                  </Typography>
                </ResizableTableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={`${template.template_id}-${template.version}`}>
                  <ResizableTableCell width={columnWidths.templateId}>
                    {template.template_id}
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.name}>
                    {template.name}
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.version}>
                    v{template.version}
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.status}>
                    <Chip
                      label={template.status}
                      color={getStatusColor(template.status)}
                      size="small"
                    />
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.created}>
                    {new Date(template.created_at).toLocaleDateString()}
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.actions}>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, template)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ResizableTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedTemplate?.status === 'draft' && (
          <MenuItem onClick={handleActivate}>Activate</MenuItem>
        )}
        {selectedTemplate?.status === 'draft' && (
          <MenuItem onClick={handleEditTemplate}>Edit</MenuItem>
        )}
        <MenuItem onClick={handleViewTemplate}>View</MenuItem>
        {selectedTemplate?.status !== 'archived' && (
          <MenuItem onClick={handleArchive}>Archive</MenuItem>
        )}
        <MenuItem onClick={handleClone}>Clone</MenuItem>
      </Menu>

      {/* Clone Template Dialog */}
      <Dialog open={cloneDialogOpen} onClose={handleCancelClone} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new template based on "{templateToClone?.name}"
          </Typography>

          <TextField
            fullWidth
            label="New Template ID"
            value={cloneFormData.template_id}
            onChange={(e) => setCloneFormData({ ...cloneFormData, template_id: e.target.value })}
            margin="normal"
            required
            helperText="Unique identifier for the new template"
          />

          <TextField
            fullWidth
            label="New Template Name"
            value={cloneFormData.name}
            onChange={(e) => setCloneFormData({ ...cloneFormData, name: e.target.value })}
            margin="normal"
            required
            helperText="Display name for the new template"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClone}>Cancel</Button>
          <Button
            onClick={handleConfirmClone}
            variant="contained"
            disabled={!cloneFormData.template_id || !cloneFormData.name}
          >
            Clone Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(StatementTemplateList);
